import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import PagePath from '../../../components/PagePath';
import Message from '../../Message';
import { getProject } from '../project/query';
import ProjectEditModal from '../projects/modal/EditProject/modal';
import BrandProfile from './BrandProfile';
import Frequency from './Frequency';
import Topics from './Topics';

class AiTrackingProjectSettings extends Component {
    constructor() {
        super();

        this.getProjectData = this.getProjectData.bind(this);
        this.onSaved = this.onSaved.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onProfileSaved = this.onProfileSaved.bind(this);
        this.onProjectUpdated = this.onProjectUpdated.bind(this);
        this.onFrequencyApplied = this.onFrequencyApplied.bind(this);
        this.deleteMessage = this.deleteMessage.bind(this);

        this.state = {
            loading: true,
            project: null,
            message: '',
            message_tags: '',
        };
    }

    componentDidMount() {
        this.getProjectData();
    }

    async getProjectData() {
        try {
            const res = await getProject(this.props.id);
            const data = await res.json();
            if (res.status === 200) {
                this.setState({
                    loading: false,
                    project: data,
                });
            } else {
                this.setState({
                    loading: false,
                    message: data?.detail ?? this.props.t('global.something_went_wrong'),
                    message_tags: 'error',
                });
            }
        } catch (err) {
            this.setState({
                loading: false,
                message: err?.message ?? this.props.t('global.something_went_wrong'),
                message_tags: 'error',
            });
        }
    }

    onSaved(project) {
        this.setState({
            project,
            message: this.props.t('ai_tracking.projects.settings.saved'),
            message_tags: 'success',
        });
    }

    onProfileSaved() {
        this.setState({
            message: this.props.t('ai_tracking.projects.settings.brand_profile.saved'),
            message_tags: 'success',
        });
    }

    onFrequencyApplied(count) {
        this.setState({
            message: this.props.t('ai_tracking.projects.settings.frequency.saved', { count }),
            message_tags: 'success',
        });
    }

    /**
     * A card below changed the project itself -- the brand profile card saving the country and
     * language it needed to generate.
     *
     * No message goes with it: the action the user actually asked for carries on right after
     * and speaks for itself, and the project block now shows the market it saved.
     */
    onProjectUpdated(project) {
        this.setState({ project });
    }

    onClose() {
        window.location.href = `/ai-tracking/project/${this.props.id}/`;
    }

    deleteMessage() {
        this.setState({ message: '' });
    }

    render() {
        const { t } = this.props;
        const { project } = this.state;

        return (
            <>
                {this.state.loading && <Loading padding={10} />}
                {!this.state.loading && !!project && (
                    <>
                        {/* The breadcrumb names the project, so it can only be rendered once
                            the project has loaded — hence here and not in the page template.
                            PagePath reads its props in the constructor only, so renaming the
                            project has to remount it. */}
                        <PagePath
                            key={project.name}
                            paths={`ai_tracking.header,layout.menuItems.keyword_manager,${project.name},ai_tracking.projects.settings.header`}
                            has_domain="true"
                            links={`/ai-tracking/,/ai-tracking/,/ai-tracking/project/${this.props.id}/`}
                        />
                        {/* The block reads `item` in its constructor only, so a market saved
                            from the brand profile card below would otherwise leave stale
                            selects here -- and saving the block would write the emptiness back
                            over what was just stored. Keying it on the market remounts it. */}
                        <ProjectEditModal
                            key={`${project.country ?? ''}-${project.language ?? ''}`}
                            asPage
                            isOpen
                            item={project}
                            title={t('ai_tracking.projects.settings.header')}
                            subtitle={t('ai_tracking.projects.settings.subtitle')}
                            closeLabel={t('ai_tracking.projects.settings.back')}
                            onSaved={this.onSaved}
                            onClose={this.onClose}
                        />
                        <BrandProfile
                            projectId={this.props.id}
                            project={project}
                            hasDomain={!!project.own_domain}
                            onSaved={this.onProfileSaved}
                            onProjectUpdated={this.onProjectUpdated}
                        />
                        <Frequency projectId={this.props.id} onApplied={this.onFrequencyApplied} />
                        <Topics projectId={this.props.id} project={project} />
                    </>
                )}
                {this.state.message && (
                    <div className="messages">
                        <Message
                            message_tags={this.state.message_tags}
                            message={this.state.message}
                            deleteMessage={this.deleteMessage}
                        />
                    </div>
                )}
            </>
        );
    }
}

export default withTranslation()(AiTrackingProjectSettings);
